import { useState } from "react";
import { format } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { CalendarIcon, RefreshCw, Users, ListChecks, FileText, Info } from "lucide-react";
import { useProductionLog } from "../hooks/useProductionLog";
import { toBangkokTimestamp, toBangkokTimestampEndOfDay } from "@/shared/lib/commission-date-utils";
import { t } from "@/shared/lib/th-translations";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Alert, AlertDescription } from "../components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";

const BANGKOK_TZ = "Asia/Bangkok";

export default function ProductionOverview() {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [calendarOpen, setCalendarOpen] = useState(false);

  const fromMs = toBangkokTimestamp(selectedDate);
  const toMs = toBangkokTimestampEndOfDay(selectedDate);

  const { data, isLoading, error, refetch } = useProductionLog({ fromMs, toMs });

  const entries = data?.data?.entries ?? [];
  const stats = data?.data?.stats;

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">{t("navigation.productionOverview")}</h1>
          <p className="text-muted-foreground">{t("production.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger>
              <Button variant="outline" className="gap-2">
                <CalendarIcon className="h-4 w-4" />
                <span className="font-mono">{format(selectedDate, "dd/MM/yy")}</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => {
                  if (date) {
                    setSelectedDate(date);
                    setCalendarOpen(false);
                  }
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>

          <Button variant="outline" onClick={() => setSelectedDate(new Date())}>
            {t("production.filters.today")}
          </Button>

          <Button variant="outline" onClick={() => void refetch()} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            {t("production.filters.refresh")}
          </Button>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t("production.log.stats.totalEvents")}
            </CardTitle>
            <ListChecks className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? (
                <Skeleton className="h-8 w-16" />
              ) : (
                (stats?.totalEvents ?? 0).toLocaleString()
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t("production.log.stats.uniqueEmployees")}
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? (
                <Skeleton className="h-8 w-16" />
              ) : (
                (stats?.uniqueEmployees ?? 0).toLocaleString()
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t("production.log.stats.uniqueTasks")}
            </CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? (
                <Skeleton className="h-8 w-16" />
              ) : (
                (stats?.uniqueTasks ?? 0).toLocaleString()
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Error state */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{t("production.log.error")}</AlertDescription>
        </Alert>
      )}

      {/* Main log table */}
      <Card>
        <CardHeader>
          <CardTitle>{t("production.log.title")}</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {[...Array(8)].map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : entries.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              {t("production.log.empty")}
            </div>
          ) : (
            <div className="overflow-x-auto -mx-6 px-6">
              <Table className="w-auto min-w-full" style={{ tableLayout: "auto" }}>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-px whitespace-nowrap px-2">
                      {t("production.log.columns.time")}
                    </TableHead>
                    <TableHead className="w-px whitespace-nowrap">
                      {t("production.log.columns.employee")}
                    </TableHead>
                    <TableHead className="w-px whitespace-nowrap">
                      {t("production.log.columns.process")}
                    </TableHead>
                    <TableHead className="w-px whitespace-nowrap">
                      {t("production.log.columns.machine")}
                    </TableHead>
                    <TableHead className="w-px whitespace-nowrap">
                      {t("production.log.columns.task")}
                    </TableHead>
                    <TableHead>{t("production.log.columns.customer")}</TableHead>
                    <TableHead className="w-px text-right whitespace-nowrap">
                      {t("production.log.columns.quantity")}
                    </TableHead>
                    <TableHead className="w-px text-right whitespace-nowrap">
                      {t("production.log.columns.targetQuantity")}
                    </TableHead>
                    <TableHead className="w-px text-right whitespace-nowrap">
                      {t("production.log.columns.shots")}
                    </TableHead>
                    <TableHead className="w-px whitespace-nowrap">
                      <span className="inline-flex items-center gap-1">
                        {t("production.log.columns.source")}
                        <SourceInfoBadge />
                      </span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map((entry, idx) => (
                    <TableRow key={`${entry.timestamp}-${entry.employeeId}-${idx}`}>
                      <TableCell className="w-px px-2 font-mono text-xs whitespace-nowrap">
                        {formatInTimeZone(new Date(entry.timestamp), BANGKOK_TZ, "HH:mm")}
                      </TableCell>
                      <TableCell className="w-px whitespace-nowrap">{entry.employeeName}</TableCell>
                      <TableCell className="w-px whitespace-nowrap">{entry.processTitle}</TableCell>
                      <TableCell className="w-px whitespace-nowrap">{entry.machine}</TableCell>
                      <TableCell className="w-px font-mono whitespace-nowrap">{entry.taskNumber}</TableCell>
                      <TableCell
                        className="max-w-[160px] truncate"
                        title={entry.customerName}
                      >
                        {entry.customerName}
                      </TableCell>
                      <TableCell className="w-px text-right font-mono whitespace-nowrap">
                        {entry.quantity.toLocaleString()}
                      </TableCell>
                      <TableCell className="w-px text-right font-mono text-muted-foreground whitespace-nowrap">
                        {entry.targetQuantity > 0
                          ? entry.targetQuantity.toLocaleString()
                          : "—"}
                      </TableCell>
                      <TableCell className="w-px text-right font-mono text-muted-foreground whitespace-nowrap">
                        {entry.shots > 0 ? entry.shots.toLocaleString() : "—"}
                      </TableCell>
                      <TableCell className="w-px whitespace-nowrap">
                        <Badge
                          className={
                            entry.source === "production_log"
                              ? "bg-blue-100 text-blue-800 hover:bg-blue-100"
                              : "bg-green-100 text-green-800 hover:bg-green-100"
                          }
                          title={t(`production.log.sourceTooltip.${entry.source}`)}
                        >
                          {t(`production.log.source.${entry.source}`)}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function SourceInfoBadge() {
  const tooltip = `${t("production.log.source.production_log")}: ${t(
    "production.log.sourceTooltip.production_log",
  )}\n\n${t("production.log.source.qc_pack")}: ${t(
    "production.log.sourceTooltip.qc_pack",
  )}`;
  return (
    <span
      className="inline-flex items-center justify-center text-muted-foreground cursor-help"
      title={tooltip}
      aria-label="source explanation"
    >
      <Info className="h-3.5 w-3.5" />
    </span>
  );
}
